import { Check, Warning } from '@phosphor-icons/react';
import { DarkPopup } from '@components/index';
import { useGlobalContext } from '@contexts/global';
import { ColoredPill, HoverBorderButton, MuteText, StyledButton } from '@elements/index';
import { useDebouncedEffect } from '@hooks/index';
import { AutoCompleteRackSearch } from '@page-components/inventory/RackSearch';
import { useState } from 'react';
import { Form, Loader } from 'semantic-ui-react';
import type {
  IMoveLocationsResponse,
  IMoveValidationResponse,
  IRackDetails,
} from 'types/api/location';
import { LocationService, theme, ToastMessage } from '@utility/index';

interface IMoveLocationFormProps {
  locationId: string;
  toggleMoveLocationView: () => void;
  onClose: () => void;
  refreshData?: () => void;
}

export function MoveLocationForm({
  locationId,
  toggleMoveLocationView,
  onClose,
  refreshData,
}: IMoveLocationFormProps) {
  const { warehouseId } = useGlobalContext();
  const [location, setLocation] = useState<IRackDetails | null>(null);
  const [loading, setLoading] = useState(false);

  const [verficationStatus, setVerificationStatus] = useState({
    loading: false,
    canMove: false,
    verifed: false,
  });

  const onResultClick = (location: IRackDetails) => {
    setLocation(location);
  };

  useDebouncedEffect(
    () => {
      if (location) {
        checkLocation();
      }
    },
    [location],
    500,
  );

  const checkLocation = async () => {
    try {
      setVerificationStatus({ canMove: false, loading: true, verifed: false });
      const payload = {
        locationIds: [locationId],
        targetRackId: location._id,
      };
      const res = (await LocationService.checkLocationCanRecieve(
        payload,
      )) as IMoveValidationResponse;

      setVerificationStatus({
        canMove: res.canMoveAll,
        loading: false,
        verifed: true,
      });
    } catch (error) {
      ToastMessage.apiError(error);
      setVerificationStatus({ canMove: false, loading: false, verifed: true });
    }
  };

  const confirmMoveLocation = async () => {
    try {
      setLoading(true);
      const payload = {
        locationIds: [locationId],
        targetRackId: location._id,
      };
      const res = (await LocationService.moveLocation(payload)) as IMoveLocationsResponse;

      if (res?.success) {
        ToastMessage.success('Location moved successfully');
        onClose();
        refreshData();
        toggleMoveLocationView();
      } else {
        ToastMessage.error('Failed to move location');
      }
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form className="mt-3" onSubmit={confirmMoveLocation}>
      <div>
        <div className="d-flex align-items-center">
          <MuteText>Search for a new Location</MuteText>
          {verficationStatus.loading ? (
            <Loader className="ml-2" active inline size="mini" />
          ) : verficationStatus.verifed ? (
            verficationStatus.canMove ? (
              <Check size={13} className="ml-2 mb-1" color={theme.colors.green} />
            ) : (
              <DarkPopup
                trigger={<Warning className="ml-2" size={13} color={theme.colors.red} />}
              >
                Location cannot be moved to selected rack
              </DarkPopup>
            )
          ) : (
            <></>
          )}
        </div>
        <AutoCompleteRackSearch warehouseId={warehouseId} onResultClick={onResultClick} />
      </div>
      {location && (
        <div className="d-flex mt-1 align-items-center">
          <MuteText className="mr-1">Selected Rack</MuteText>
          <ColoredPill color={location.zoneId.color}>{location.zoneId.code}</ColoredPill>
          <ColoredPill className="ml-1" color={location.zoneId.color}>
            {location.code}
          </ColoredPill>
        </div>
      )}
      <div className="d-flex justify-content-between mt-4">
        <HoverBorderButton
          disabled={loading}
          onClick={toggleMoveLocationView}
          type="button"
        >
          Back to Edit
        </HoverBorderButton>
        <div className="d-flex">
          <HoverBorderButton type="button" onClick={onClose}>
            Close
          </HoverBorderButton>
          <StyledButton
            type="submit"
            disabled={verficationStatus.loading || !verficationStatus.canMove}
            loading={loading}
            className="ml-1"
          >
            Move
          </StyledButton>
        </div>
      </div>
    </Form>
  );
}
